import React, {useEffect, useState} from 'react';
import {useRouter} from 'next/router';
import cx from 'classnames';

import Button, {ButtonTypes} from '../../components/ButtonNew';
import Link from '../../components/Link';
import {ExternalIcon} from '../../components/svg';
import DocumentHeader from '../../components/DocumentHeader';
import {createURLWithParams} from '../../util';
import {getTags} from '../../api/api';
import {useAppContext, useAnalytics} from '../../hooks';
import jobData from './jobs';

const Roles = [{
  name: 'Product Management',
  value: 'pm',
}, {
  name: 'Software Engineering',
  value: 'swe',
}, {
  name: 'Engineering Management',
  value: 'em',
}];

const Badge = ({children}) => (
  <span className="px-3 py-1 bg-gray-100 rounded-full font-medium text-xs text-gray-500 whitespace-nowrap">
    {children}
  </span>
);

const RoleChiclet = ({
  onClick,
  name,
  value,
  selected,
}) => (
  <Button
    key={value}
    buttonType={ButtonTypes.SECONDARY}
    onClick={(e) => onClick(e, value)}
    href={createURLWithParams('/jobs', {role: value})}
    className={cx('whitespace-nowrap rounded-full', {
      'text-indigo-600 border-indigo-600': selected,
    })}
  >
    {name}
  </Button>
);

const JobRow = ({job, logo, onClickApply}) => (
  <div className="flex flex-col md:flex-row items-start md:items-center py-6 w-full justify-between">
    <div className="flex items-start md:items-center gap-4">
      <Link href={`/companies/${job.companyTag}?src=jobs`} className="flex items-center w-14 h-14 md:w-18 md:h-18 p-3 md:p-3 rounded-2xl bg-white shadow-md border border-gray-100">
        <img src={logo} alt="" />
      </Link>
      <div className="flex flex-col self-center gap-0.5">
        <Link
          className="text-indigo underline font-medium text-sm"
          href={`/companies/${job.companyTag}?src=jobs`}
        >
          {job.company}
        </Link>
        <span className="text-base md:text-lg font-semibold text-gray-900">
          {job.title}
        </span>
        <div className="hidden md:flex gap-2 -ml-0.5 overflow-auto scrollbar-hidden">
          {job.location && <Badge>{job.location}</Badge>}
          {job.remote && <Badge>Remote</Badge>}
          {job.tags && job.tags.map((tag) => <Badge>{tag}</Badge>)}
          {job.salaryRange && <Badge>{job.salaryRange}</Badge>}
        </div>
      </div>
    </div>
    <div className="w-full flex md:hidden mt-4 gap-2 overflow-auto scrollbar-hidden">
      {job.location && <Badge>{job.location}</Badge>}
      {job.remote && <Badge>Remote</Badge>}
      {job.salaryRange && <Badge>{job.salaryRange}</Badge>}
      {job.tags && job.tags.map((tag) => <Badge>{tag}</Badge>)}
    </div>
    <div className="flex w-full md:w-auto mt-4 md:mt-0">
      <Button
        buttonType={ButtonTypes.SECONDARY}
        href={job.jobURL}
        onClick={() => onClickApply(job)}
        target="_blank"
        className="w-full md:w-auto"
      >
        Apply now
        <ExternalIcon className="ml-2 h-4 w-4" />
      </Button>
    </div>
  </div>
);

const JobsPage = () => {
  const [selectedRole, setSelectedRole] = useState(null);
  const [companies, setCompanies] = useState(null);
  const {currentUser} = useAppContext();
  const {tracker} = useAnalytics();
  const router = useRouter();
  const {jobs} = jobData;

  useEffect(() => {
    if (router.isReady) {
      const role = Roles.find((r) => r.value === router.query.role);
      tracker.jobsView({role: role?.value});
      setSelectedRole(role);
    }
  }, [router.query, router.isReady]);

  useEffect(() => {
    getTags().then(setCompanies);
  }, []);

  const handleRoleChange = (e, role) => {
    e.preventDefault();

    if (!selectedRole || selectedRole.value !== role) {
      router.push({query: {role}});
    }
  };

  const onClickApply = ({title, company, role}) => {
    tracker.jobsClickApply({role, company, title});
  };

  const getCompanyLogo = (id) => companies?.[id]?.image_url;

  const filteredJobs = selectedRole
    ? jobs?.filter((job) => job.role === selectedRole.value)
    : jobs;

  const CALLOUT_INDEX = 4;

  return (
    <div className="bg-white min-h-screen">
      <DocumentHeader
        title="Top tech jobs this week"
        socialDescription="Explore the latest job postings from top tech companies"
        imgPath="/images/jobs_cover_image.png"
      />
      <div className="pt-8 md:pt-12 container max-w-4xl bg-white">
        <h1 className="text-2xl md:text-4xl font-bold">
          {selectedRole
            ? `Top ${selectedRole?.name} jobs this week`
            : 'Top tech jobs to apply to this week'}
        </h1>
        <div className="my-2 text-sm md:text-base text-gray-600 font-medium">
          Welcome to Exponent&rsquo;s curated job list, where we bring you the latest job postings
          from top tech companies.
        </div>
        {/* <span className="">
          {`Last updated ${new Date(lastUpdated)}`}
        </span> */}
        <div className="mt-8 flex gap-2 overflow-auto py-1 -mx-4 px-4 scrollbar-hidden">
          <RoleChiclet
            name="All"
            value={undefined}
            selected={!selectedRole}
            onClick={handleRoleChange}
          />
          {Roles.map((role) => (
            <RoleChiclet
              name={role.name}
              value={role.value}
              selected={selectedRole?.value === role.value}
              onClick={handleRoleChange}
            />
          ))}
        </div>
        <div className="mt-2 flex flex-col divide-y -mx-4 md:mx-0 px-4 md:px-0">
          {filteredJobs.slice(0, CALLOUT_INDEX).map((job) => (
            <JobRow
              job={job}
              logo={getCompanyLogo(job.companyTag)}
              onClickApply={onClickApply}
            />
          ))}

          {/* Callout CTA inserted into list */}
          <Link href="/resume?src=jobs" className="-mx-8 rounded-xl bg-gradient-to-r from-indigo-700 to-purple-700 shadow-xl">
            <div className="flex flex-col md:flex-row justify-between md:items-center w-full py-4 px-8 md:px-3 sm:py-6 sm:px-6 lg:px-8 space-y-2 md:space-y-0">
              <h3 className="text-lg md:text-xl font-bold tracking-tight text-white leading-tight">
                <span className="mb-1 block">Applying? Get your resume reviewed.</span>
                <span className="text-base font-normal opacity-75">Perfect your resume with help from a FAANG recruiter.</span>
              </h3>
              <div
                href="/resume?src=jobs"
                className="whitespace-nowrap inline-flex w-full items-center justify-center rounded-md border border-transparent bg-white px-4 py-2 text-sm font-medium text-indigo-600 hover:bg-indigo-50 md:w-auto"
              >
                {'Learn more ->'}
              </div>
            </div>
          </Link>

          {/* Remainder of list */}
          {filteredJobs.slice(CALLOUT_INDEX).map((job) => (
            <JobRow
              job={job}
              logo={getCompanyLogo(job.companyTag)}
              onClickApply={onClickApply}
            />
          ))}

          {/* Empty state */}
          {filteredJobs?.length === 0 && (
            <span className="mt-8 text-center font-medium text-gray-700">
              Sorry, no matching jobs right now. Check back later.
            </span>
          )}
        </div>
        <div className="my-10 flex flex-col md:flex-row gap-6">
          <div className="rounded-xl bg-gradient-to-r from-indigo-700 to-purple-700 shadow-xl">
            <div className="mx-auto max-w-2xl py-8 px-8 md:px-4 text-center sm:py-10 sm:px-6 lg:px-8">
              <h3 className="text-2xl md:text-2xl font-bold tracking-tight text-white sm:text-4xl">
                <span className="block">Practice interviewing, perfect your resume, and more.</span>
              </h3>
              <Link
                href={currentUser ? '/coaching?src=jobs' : '/signup?src=jobs'}
                className="mt-8 inline-flex w-full items-center justify-center rounded-md border border-transparent bg-white px-5 py-3 text-base font-medium text-indigo-600 hover:bg-indigo-50 sm:w-auto"
              >
                {currentUser ? 'Learn more ->' : 'Create your free account'}
              </Link>
            </div>
          </div>
          <div className="rounded-xl bg-gradient-to-r from-blue-700 to-blue-700 shadow-xl">
            <div className="mx-auto max-w-2xl py-8 px-8 md:px-4 text-center sm:py-10 sm:px-6 lg:px-8">
              <h3 className="text-2xl md:text-2xl font-bold tracking-tight text-white sm:text-4xl">
                <span className="block">Get referred to top companies by our community</span>
              </h3>
              <Link
                href="/jobs/referrals?src=jobs"
                className="mt-8 inline-flex w-full items-center justify-center rounded-md border border-transparent bg-white px-5 py-3 text-base font-medium text-indigo-600 hover:bg-indigo-50 sm:w-auto"
              >
                {'Get referred ->'}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JobsPage;
